import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMagnifyingGlass, faPlus, faPenToSquare, faTrash, faTimes,
    faBook, faRotate, faMapMarkerAlt, faArrowUpRightFromSquare, faLayerGroup,
    faHandshake, faGasPump, faShieldHalved, faCoins, faShip,
    faFileCircleCheck, faFileSignature, faAnchor, faDroplet, faIdCard,
    faUserShield, faBan, faReceipt, faPercent, faSatelliteDish, faLeaf,
    faLocationDot,
} from '@fortawesome/free-solid-svg-icons';
import { useHistory } from 'react-router-dom';
import { useFlashMessage } from './FlashMessage';
import glossaryService from '../services/glossaryService';
import './HamsterGlossary.css';

// Keyword -> FontAwesome icon. Admins pick a keyword; unknown -> book.
const ICON_MAP = {
    handshake: faHandshake, gas: faGasPump, shield: faShieldHalved,
    coins: faCoins, ship: faShip, 'file-check': faFileCircleCheck,
    'file-signature': faFileSignature, search: faMagnifyingGlass,
    anchor: faAnchor, droplet: faDroplet, id: faIdCard,
    'user-shield': faUserShield, ban: faBan, receipt: faReceipt,
    percent: faPercent, satellite: faSatelliteDish, leaf: faLeaf,
    location: faLocationDot, book: faBook,
};
const ICON_CHOICES = Object.keys(ICON_MAP);
const iconFor = (kw) => ICON_MAP[kw] || faBook;

const DIFFICULTY_META = {
    rookie: { label: 'Rookie', color: '#4E8C2B' },
    trader: { label: 'Trader', color: '#C77D12' },
    veteran: { label: 'Veteran', color: '#B03636' },
};

const FALLBACK_COLOR = '#5F5E5A';

function hexToRgba(hex, a) {
    if (!hex) return `rgba(95,94,90,${a})`;
    let h = hex.replace('#', '');
    if (h.length === 3) h = h.split('').map((x) => x + x).join('');
    const n = parseInt(h, 16);
    if (Number.isNaN(n)) return `rgba(95,94,90,${a})`;
    return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}

const HamsterGlossary = () => {
    const { showSuccess, showError } = useFlashMessage();
    const history = useHistory();

    const [categories, setCategories] = useState([]);
    const [terms, setTerms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [canEdit, setCanEdit] = useState(false);

    const [query, setQuery] = useState('');
    const [activeCat, setActiveCat] = useState('all');
    const [flipped, setFlipped] = useState(() => new Set());

    const [termModal, setTermModal] = useState(null); // null | {} (new) | term (edit)
    const [catModalOpen, setCatModalOpen] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [cats, tms, perms] = await Promise.all([
                glossaryService.getCategories(),
                glossaryService.getTerms(),
                glossaryService.getPermissions(),
            ]);
            setCategories(cats);
            setTerms(tms);
            setCanEdit(!!(perms && perms.can_edit));
        } catch (err) {
            showError('Could not load the glossary. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return terms.filter((t) => {
            const slug = t.category_detail ? t.category_detail.slug : null;
            const okCat = activeCat === 'all' || slug === activeCat;
            if (!okCat) return false;
            if (!q) return true;
            const hay = `${t.term} ${t.acronym_expansion || ''} ${t.definition} ${t.category_detail ? t.category_detail.name : ''}`.toLowerCase();
            return hay.includes(q);
        });
    }, [terms, query, activeCat]);

    const toggleFlip = (id) => {
        setFlipped((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const focusRelated = (rel) => {
        setActiveCat('all');
        setQuery(rel.term);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (term) => {
        // eslint-disable-next-line no-alert
        if (!window.confirm(`Delete "${term.term}" from the glossary? This cannot be undone.`)) return;
        try {
            await glossaryService.deleteTerm(term.id);
            showSuccess(`Deleted "${term.term}".`);
            load();
        } catch (err) {
            showError('Delete failed. You may not have permission.');
        }
    };

    const renderCard = (t) => {
        const color = (t.category_detail && t.category_detail.color) || FALLBACK_COLOR;
        const diff = DIFFICULTY_META[t.difficulty] || DIFFICULTY_META.rookie;
        const isFlipped = flipped.has(t.id);
        const related = t.related_terms_detail || [];
        return (
            <div
                key={t.id}
                className={`hglos-card ${isFlipped ? 'flipped' : ''}`}
                onClick={() => toggleFlip(t.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleFlip(t.id); } }}
            >
                <div className="hglos-inner">
                    {/* Front */}
                    <div className="hglos-face hglos-face--front">
                        <div className="hglos-card-top">
                            <span className="hglos-icon" style={{ background: hexToRgba(color, 0.14), color }}>
                                <FontAwesomeIcon icon={iconFor(t.icon || (t.category_detail && t.category_detail.icon))} />
                            </span>
                            <div className="hglos-top-right" onClick={(e) => e.stopPropagation()}>
                                {canEdit && (
                                    <>
                                        <button type="button" className="hglos-cardbtn" title="Edit term" aria-label="Edit term" onClick={() => setTermModal(t)}>
                                            <FontAwesomeIcon icon={faPenToSquare} />
                                        </button>
                                        <button type="button" className="hglos-cardbtn danger" title="Delete term" aria-label="Delete term" onClick={() => handleDelete(t)}>
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    </>
                                )}
                                <span className="hglos-dot" style={{ background: diff.color }} title={diff.label} />
                            </div>
                        </div>
                        <div className="hglos-term">
                            {t.term}
                            {!t.is_published && <span className="hglos-draft">Draft</span>}
                        </div>
                        {t.acronym_expansion && <div className="hglos-expansion">{t.acronym_expansion}</div>}
                        <p className="hglos-preview">{t.definition}</p>
                        <div className="hglos-card-bottom">
                            {t.category_detail && (
                                <span className="hglos-cat-chip" style={{ background: hexToRgba(color, 0.14), color }}>
                                    {t.category_detail.name}
                                </span>
                            )}
                            <span className="hglos-flip-hint"><FontAwesomeIcon icon={faRotate} /> flip</span>
                        </div>
                    </div>

                    {/* Back */}
                    <div className="hglos-face hglos-face--back" style={{ borderColor: hexToRgba(color, 0.5) }}>
                        <div className="hglos-def">{t.definition}</div>
                        <div className="hglos-back-foot">
                            {t.seen_in && (
                                <div
                                    className={`hglos-seen ${t.module_link ? 'is-link' : ''}`}
                                    onClick={(e) => { if (t.module_link) { e.stopPropagation(); history.push(t.module_link); } }}
                                >
                                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                                    <span>Seen in: {t.seen_in}</span>
                                    {t.module_link && <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="hglos-seen-go" />}
                                </div>
                            )}
                            {related.length > 0 && (
                                <div className="hglos-related" onClick={(e) => e.stopPropagation()}>
                                    {related.map((r) => (
                                        <button type="button" key={r.id} className="hglos-related-chip" onClick={() => focusRelated(r)}>
                                            {r.term}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="hglos-root">
            {/* Intro strip */}
            <div className="hglos-intro">
                <div className="hglos-intro-left">
                    <span className="hglos-intro-icon"><FontAwesomeIcon icon={faBook} /></span>
                    <div>
                        <div className="hglos-intro-title">IMT Lexicon</div>
                        <div className="hglos-intro-sub">
                            Decode the language of IMT · {terms.length} term{terms.length === 1 ? '' : 's'}
                        </div>
                    </div>
                </div>
                {canEdit && (
                    <div className="hglos-intro-actions">
                        <button type="button" className="hglos-btn" onClick={() => setCatModalOpen(true)}>
                            <FontAwesomeIcon icon={faLayerGroup} /> Manage categories
                        </button>
                        <button type="button" className="hglos-btn primary" onClick={() => setTermModal({})}>
                            <FontAwesomeIcon icon={faPlus} /> Add term
                        </button>
                    </div>
                )}
            </div>

            {/* Toolbar: full-width search */}
            <div className="hglos-toolbar">
                <div className="hglos-search">
                    <FontAwesomeIcon icon={faMagnifyingGlass} className="hglos-search-icon" />
                    <input
                        type="text"
                        placeholder="Search a term, e.g. BDN, recap, sanctions…"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        aria-label="Search glossary"
                    />
                    {query && (
                        <button type="button" className="hglos-search-clear" aria-label="Clear search" onClick={() => setQuery('')}>
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    )}
                </div>
            </div>

            {/* Filters row: category chips on the left, difficulty legend right-aligned. */}
            <div className="hglos-chips">
                <button
                    type="button"
                    className={`hglos-chip ${activeCat === 'all' ? 'active' : ''}`}
                    onClick={() => setActiveCat('all')}
                >
                    All
                </button>
                {categories.map((c) => {
                    const on = activeCat === c.slug;
                    return (
                        <button
                            type="button"
                            key={c.id}
                            className={`hglos-chip ${on ? 'active' : ''}`}
                            style={on ? { background: c.color, borderColor: c.color, color: '#fff' } : { color: c.color, borderColor: hexToRgba(c.color, 0.4) }}
                            onClick={() => setActiveCat(c.slug)}
                        >
                            {c.name}
                        </button>
                    );
                })}
                <div className="hglos-legend">
                    {Object.keys(DIFFICULTY_META).map((k) => (
                        <span key={k} className="hglos-legend-item">
                            <span className="hglos-dot" style={{ background: DIFFICULTY_META[k].color }} />
                            {DIFFICULTY_META[k].label}
                        </span>
                    ))}
                </div>
            </div>

            {/* Grid */}
            {loading ? (
                <div className="hglos-loading"><div className="hglos-spinner" /><p>Loading the lexicon…</p></div>
            ) : filtered.length === 0 ? (
                <div className="hglos-empty">
                    <FontAwesomeIcon icon={faBook} className="hglos-empty-icon" />
                    <h3>No terms match</h3>
                    <p>{query ? `Nothing for “${query}”.` : 'Nothing in this category yet.'}{canEdit ? ' Add one with “Add term”.' : ''}</p>
                </div>
            ) : (
                <div className="hglos-grid">{filtered.map(renderCard)}</div>
            )}

            {termModal && (
                <TermFormModal
                    term={termModal.id ? termModal : null}
                    categories={categories}
                    allTerms={terms}
                    onClose={() => setTermModal(null)}
                    onSaved={() => { setTermModal(null); load(); }}
                    showSuccess={showSuccess}
                    showError={showError}
                />
            )}

            {catModalOpen && (
                <CategoryManagerModal
                    categories={categories}
                    onClose={() => setCatModalOpen(false)}
                    onChanged={load}
                    showSuccess={showSuccess}
                    showError={showError}
                />
            )}
        </div>
    );
};

/* ---------------- Term add/edit modal ---------------- */
function TermFormModal({ term, categories, allTerms, onClose, onSaved, showSuccess, showError }) {
    const isEdit = !!term;
    const [saving, setSaving] = useState(false);
    const [relFilter, setRelFilter] = useState('');
    const [form, setForm] = useState(() => ({
        term: (term && term.term) || '',
        acronym_expansion: (term && term.acronym_expansion) || '',
        definition: (term && term.definition) || '',
        category: (term && term.category) || (categories[0] && categories[0].id) || '',
        difficulty: (term && term.difficulty) || 'rookie',
        icon: (term && term.icon) || '',
        seen_in: (term && term.seen_in) || '',
        module_link: (term && term.module_link) || '',
        is_published: term ? term.is_published : true,
        related_terms: (term && term.related_terms) || [],
    }));

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const toggleRelated = (id) => set('related_terms',
        form.related_terms.includes(id)
            ? form.related_terms.filter((x) => x !== id)
            : [...form.related_terms, id]);

    const selectedCat = categories.find((c) => c.id === Number(form.category));
    const previewIcon = iconFor(form.icon || (selectedCat && selectedCat.icon));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.term.trim() || !form.definition.trim()) {
            showError('Term and definition are required.');
            return;
        }
        setSaving(true);
        const payload = {
            term: form.term.trim(),
            acronym_expansion: form.acronym_expansion.trim(),
            definition: form.definition.trim(),
            category: form.category || null,
            difficulty: form.difficulty,
            icon: form.icon,
            seen_in: form.seen_in.trim(),
            module_link: form.module_link.trim(),
            is_published: form.is_published,
            related_terms: form.related_terms,
        };
        try {
            if (isEdit) {
                await glossaryService.updateTerm(term.id, payload);
                showSuccess(`Updated "${payload.term}".`);
            } else {
                await glossaryService.createTerm(payload);
                showSuccess(`Added "${payload.term}".`);
            }
            onSaved();
        } catch (err) {
            showError('Save failed. Check the fields and your permissions.');
            setSaving(false);
        }
    };

    const relChoices = allTerms
        .filter((t) => !isEdit || t.id !== term.id)
        .filter((t) => !relFilter || t.term.toLowerCase().includes(relFilter.toLowerCase()));

    return (
        <div className="hglos-overlay" onMouseDown={onClose}>
            <div className="hglos-modal" onMouseDown={(e) => e.stopPropagation()}>
                <div className="hglos-modal-head">
                    <h3>{isEdit ? 'Edit term' : 'Add term'}</h3>
                    <button type="button" className="hglos-x" onClick={onClose} aria-label="Close"><FontAwesomeIcon icon={faTimes} /></button>
                </div>
                <form className="hglos-form" onSubmit={submit}>
                    <div className="hglos-row two">
                        <label>
                            <span>Term <em>*</em></span>
                            <input type="text" value={form.term} onChange={(e) => set('term', e.target.value)} placeholder="e.g. BDN" autoFocus />
                        </label>
                        <label>
                            <span>Full form / expansion</span>
                            <input type="text" value={form.acronym_expansion} onChange={(e) => set('acronym_expansion', e.target.value)} placeholder="e.g. Bunker Delivery Note" />
                        </label>
                    </div>

                    <label>
                        <span>Definition <em>*</em></span>
                        <textarea rows={3} value={form.definition} onChange={(e) => set('definition', e.target.value)} placeholder="Plain-English meaning…" />
                    </label>

                    <div className="hglos-row three">
                        <label>
                            <span>Category</span>
                            <select value={form.category} onChange={(e) => set('category', e.target.value)}>
                                <option value="">— None —</option>
                                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </label>
                        <label>
                            <span>Difficulty</span>
                            <select value={form.difficulty} onChange={(e) => set('difficulty', e.target.value)}>
                                {Object.keys(DIFFICULTY_META).map((k) => <option key={k} value={k}>{DIFFICULTY_META[k].label}</option>)}
                            </select>
                        </label>
                        <label>
                            <span>Icon</span>
                            <div className="hglos-icon-pick">
                                <span className="hglos-icon-preview"><FontAwesomeIcon icon={previewIcon} /></span>
                                <select value={form.icon} onChange={(e) => set('icon', e.target.value)}>
                                    <option value="">(inherit category)</option>
                                    {ICON_CHOICES.map((k) => <option key={k} value={k}>{k}</option>)}
                                </select>
                            </div>
                        </label>
                    </div>

                    <div className="hglos-row two">
                        <label>
                            <span>Seen in IMT</span>
                            <input type="text" value={form.seen_in} onChange={(e) => set('seen_in', e.target.value)} placeholder="e.g. Deliveries" />
                        </label>
                        <label>
                            <span>Deep-link route (optional)</span>
                            <input type="text" value={form.module_link} onChange={(e) => set('module_link', e.target.value)} placeholder="e.g. /crm" />
                        </label>
                    </div>

                    <label className="hglos-related-field">
                        <span>Related terms</span>
                        <input type="text" className="hglos-rel-filter" value={relFilter} onChange={(e) => setRelFilter(e.target.value)} placeholder="Filter terms…" />
                        <div className="hglos-rel-list">
                            {relChoices.length === 0 && <div className="hglos-rel-empty">No terms.</div>}
                            {relChoices.map((t) => (
                                <label key={t.id} className="hglos-rel-item">
                                    <input type="checkbox" checked={form.related_terms.includes(t.id)} onChange={() => toggleRelated(t.id)} />
                                    <span>{t.term}</span>
                                </label>
                            ))}
                        </div>
                    </label>

                    <label className="hglos-check">
                        <input type="checkbox" checked={form.is_published} onChange={(e) => set('is_published', e.target.checked)} />
                        <span>Published (visible to everyone). Uncheck to keep as an admin-only draft.</span>
                    </label>

                    <div className="hglos-modal-foot">
                        <button type="button" className="hglos-btn ghost" onClick={onClose} disabled={saving}>Cancel</button>
                        <button type="submit" className="hglos-btn primary" disabled={saving}>
                            {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Add term')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

/* ---------------- Category manager modal ---------------- */
function CategoryManagerModal({ categories, onClose, onChanged, showSuccess, showError }) {
    const [busy, setBusy] = useState(false);
    const [draft, setDraft] = useState({ name: '', color: '#4a6b25', icon: 'book' });

    const saveExisting = async (cat, patch) => {
        setBusy(true);
        try {
            await glossaryService.updateCategory(cat.id, patch);
            showSuccess(`Saved "${patch.name || cat.name}".`);
            onChanged();
        } catch (err) { showError('Could not save category.'); } finally { setBusy(false); }
    };

    const remove = async (cat) => {
        // eslint-disable-next-line no-alert
        if (!window.confirm(`Delete category "${cat.name}"? Its terms become uncategorised (they are not deleted).`)) return;
        setBusy(true);
        try {
            await glossaryService.deleteCategory(cat.id);
            showSuccess(`Deleted "${cat.name}".`);
            onChanged();
        } catch (err) { showError('Could not delete category.'); } finally { setBusy(false); }
    };

    const add = async () => {
        if (!draft.name.trim()) { showError('Category name is required.'); return; }
        setBusy(true);
        try {
            await glossaryService.createCategory({
                name: draft.name.trim(), color: draft.color, icon: draft.icon,
                display_order: categories.length + 1,
            });
            showSuccess(`Added "${draft.name.trim()}".`);
            setDraft({ name: '', color: '#4a6b25', icon: 'book' });
            onChanged();
        } catch (err) { showError('Could not add category (name may already exist).'); } finally { setBusy(false); }
    };

    return (
        <div className="hglos-overlay" onMouseDown={onClose}>
            <div className="hglos-modal" onMouseDown={(e) => e.stopPropagation()}>
                <div className="hglos-modal-head">
                    <h3>Manage categories</h3>
                    <button type="button" className="hglos-x" onClick={onClose} aria-label="Close"><FontAwesomeIcon icon={faTimes} /></button>
                </div>
                <div className="hglos-catmgr">
                    {categories.map((c) => (
                        <CategoryRow key={c.id} cat={c} busy={busy} onSave={saveExisting} onRemove={remove} />
                    ))}

                    <div className="hglos-catnew">
                        <div className="hglos-catnew-title"><FontAwesomeIcon icon={faPlus} /> New category</div>
                        <div className="hglos-catrow">
                            <input type="text" placeholder="Name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
                            <input type="color" value={draft.color} onChange={(e) => setDraft({ ...draft, color: e.target.value })} title="Colour" />
                            <select value={draft.icon} onChange={(e) => setDraft({ ...draft, icon: e.target.value })}>
                                {ICON_CHOICES.map((k) => <option key={k} value={k}>{k}</option>)}
                            </select>
                            <span className="hglos-icon-preview sm"><FontAwesomeIcon icon={iconFor(draft.icon)} /></span>
                            <button type="button" className="hglos-btn primary sm" onClick={add} disabled={busy}>Add</button>
                        </div>
                    </div>
                </div>
                <div className="hglos-modal-foot">
                    <button type="button" className="hglos-btn ghost" onClick={onClose}>Done</button>
                </div>
            </div>
        </div>
    );
}

function CategoryRow({ cat, busy, onSave, onRemove }) {
    const [name, setName] = useState(cat.name);
    const [color, setColor] = useState(cat.color || FALLBACK_COLOR);
    const [icon, setIcon] = useState(cat.icon || 'book');
    const [active, setActive] = useState(cat.is_active);
    const dirty = name !== cat.name || color !== (cat.color || FALLBACK_COLOR) || icon !== (cat.icon || 'book') || active !== cat.is_active;

    return (
        <div className="hglos-catrow">
            <span className="hglos-icon-preview sm" style={{ color }}><FontAwesomeIcon icon={iconFor(icon)} /></span>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} />
            <input type="color" value={color} onChange={(e) => setColor(e.target.value)} title="Colour" />
            <select value={icon} onChange={(e) => setIcon(e.target.value)}>
                {ICON_CHOICES.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
            <label className="hglos-catactive" title="Active (visible to everyone)">
                <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> live
            </label>
            <button type="button" className="hglos-btn primary sm" disabled={busy || !dirty} onClick={() => onSave(cat, { name: name.trim(), color, icon, is_active: active })}>Save</button>
            <button type="button" className="hglos-btn danger sm" disabled={busy} onClick={() => onRemove(cat)} aria-label="Delete category"><FontAwesomeIcon icon={faTrash} /></button>
        </div>
    );
}

export default HamsterGlossary;
